'use client';

/**
 * OSLCustodyChart — "Five billion Hong Kong dollars of client assets. Two
 * companies hold it." OSL Group's client digital assets held on trust
 * (FY2023 vs FY2024, Note 10 of the FY2024 results) beside the licence →
 * custody ownership chain the SFC associated-entity rule requires.
 * The staggered build plays once when the card scrolls into view and is
 * skipped for prefers-reduced-motion. No props; injects scoped styles.
 */

import React, { useEffect, useRef, useState } from 'react';

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/* ------------------------------------------------------------------ data -- */

const FIGURES = {
  prior: { period: 'FY2023', value: 874_731_000, label: 'HK$874.7M' },
  current: { period: 'FY2024', value: 5_004_094_000, label: 'HK$5,004.1M' },
} as const;

/** Height in px of the taller bar. The shorter bar is derived, never hand-set. */
const PLOT_HEIGHT = 200;

const barHeights = {
  current: PLOT_HEIGHT,
  prior: Math.round((PLOT_HEIGHT * FIGURES.prior.value) / FIGURES.current.value),
};

/* ----------------------------------------------------------------- hooks -- */

const STYLE_ID = 'osl-custody-styles';

function useInjectedStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID;
      s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

function useInView(ref: React.RefObject<HTMLDivElement | null>): boolean {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || typeof IntersectionObserver === 'undefined') {
      const t = window.setTimeout(() => setInView(true), 0);
      return () => window.clearTimeout(t);
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, inView]);
  return inView;
}

/** Stagger helper: every animated element gets its own delay. */
const at = (seconds: number) =>
  ({ ['--osl-delay' as string]: `${seconds}s` }) as React.CSSProperties;

/* ------------------------------------------------------------- component -- */

export default function OSLCustodyChart() {
  useInjectedStyles();
  const ref = useRef<HTMLDivElement>(null);
  const played = useInView(ref);
  const [width, setWidth] = useState(1000);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (): void => setWidth(el.offsetWidth);
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, []);

  const mid = width < 920; // tighter two-column layout for blog-width columns
  const narrow = width < 720; // below this the two columns stack
  const compact = width < 640;

  return (
    <div
      ref={ref}
      data-played={played ? 'true' : 'false'}
      className={`osl-root${mid ? ' mid' : ''}${narrow ? ' narrow' : ''}${compact ? ' compact' : ''}`}
    >
      <div className="osl-head">
        <h1 className="osl-h1">
          <span className="osl-rise" style={at(0)}>
            Five billion Hong Kong dollars
          </span>
          <span className="osl-rise" style={at(0.09)}>
            of client assets.
          </span>
          <span className="osl-rise osl-accent" style={at(0.18)}>
            Two companies hold it.
          </span>
        </h1>
        <img className="osl-logo" src={PSGLOBAL_LOGO} alt="PS Global Consulting" />
      </div>

      <p className="osl-standfirst osl-rise" style={at(0.3)}>
        OSL keeps client digital assets on trust — off its own balance sheet, and
        entirely its responsibility. Hong Kong&rsquo;s rules put that obligation inside
        two entities: the SFC-licensed platform, and the custody company it is
        required to own.
      </p>

      <div className="osl-cols">
        {/* ------------------------------------------------ the money -- */}
        <section>
          <div className="osl-eyebrow osl-rise" style={at(0.45)}>
            Client digital assets held on trust
          </div>

          <div className="osl-hero osl-rise" style={at(0.52)}>
            <span className="osl-hero-cur">HK$</span>
            <span className="osl-hero-num">5.0</span>
            <span className="osl-hero-unit">bn</span>
          </div>

          <div className="osl-hero-sub osl-rise" style={at(0.62)}>
            at 31 December 2024, in segregated client wallets
          </div>

          <figure className="osl-plot">
            <div className="osl-bars">
              <Bar
                value={FIGURES.prior.label}
                height={barHeights.prior}
                tone="prior"
                growDelay={0.82}
                labelDelay={1.12}
              />
              <Bar
                value={FIGURES.current.label}
                height={barHeights.current}
                tone="current"
                growDelay={0.95}
                labelDelay={1.3}
              />
            </div>

            <div className="osl-axis osl-draw-x" style={at(0.74)} />

            <div className="osl-xlabels">
              <span className="osl-rise" style={at(1.24)}>
                {FIGURES.prior.period}
              </span>
              <span className="osl-rise" style={at(1.32)}>
                {FIGURES.current.period}
              </span>
            </div>

            <figcaption className="osl-plot-note osl-rise" style={at(1.46)}>
              The jump coincides with Hong Kong&rsquo;s spot digital-asset ETFs,
              launched April 2024 — OSL says it custodies about 64% of that AUM — and
              with a bitcoin price that roughly doubled over the year.
            </figcaption>
          </figure>
        </section>

        {/* -------------------------------------------- the structure -- */}
        <section>
          <div className="osl-eyebrow osl-rise" style={at(0.7)}>
            Who holds it
          </div>

          <div className="osl-chain">
            <div className="osl-node osl-node--group osl-rise" style={at(0.8)}>
              <div className="osl-node-name">
                OSL Group Limited <span className="osl-ticker">· HKEX 863</span>
              </div>
            </div>

            <Connector delay={0.94} />

            <div className="osl-node osl-node--licence osl-rise" style={at(1.06)}>
              <span className="osl-tag osl-tag--gold">The licence</span>
              <div className="osl-node-name">OSL Digital Securities Limited</div>
              <div className="osl-node-role">
                Licensed by the SFC — Type 1, 4, 7 and 9, plus a virtual-asset trading
                platform licence under the AMLO.
              </div>
            </div>

            <Connector delay={1.2} />

            <div className="osl-node osl-node--custody osl-rise" style={at(1.32)}>
              <span className="osl-tag osl-tag--teal">The assets</span>
              <div className="osl-node-name">OSL Custody Services Limited</div>
              <div className="osl-node-role">
                The &ldquo;associated entity&rdquo; required by the rules. Holds the{' '}
                <strong className="osl-emph">HK$5.0bn</strong> on trust, in wallets
                segregated from the group&rsquo;s own.
              </div>
            </div>
          </div>

          <p className="osl-chain-note osl-rise" style={at(1.58)}>
            The custody company sits <strong>under</strong> the licensed platform, not
            beside it — that direct ownership is what the associated-entity rule
            requires.
          </p>
        </section>
      </div>

      {/* ------------------------------------------------------ foot -- */}
      <div className="osl-foot">
        <div className="osl-rule osl-draw-x" style={at(1.74)} />
        <p className="osl-kicker osl-rise" style={at(1.86)}>
          The assets are not on OSL&rsquo;s balance sheet.{' '}
          <em className="osl-accent">The obligation is.</em>
        </p>
        <p className="osl-src osl-rise" style={at(2.0)}>
          Sources — OSL Group (HKEX: 863) FY2024 results announcement, 24 Mar 2025,
          Note 10: client digital assets of HK$5,004,094,000 (FY2024) and
          HK$874,731,000 (FY2023), safekept in segregated client wallets through a
          trust arrangement with OSL Custody Services Limited and &ldquo;not
          recognised as the Group&rsquo;s digital assets&rdquo;. Ownership chain per
          GLEIF records for OSL Custody Services Limited (direct parent: OSL Digital
          Securities Limited). OSL&rsquo;s own results release describes assets under
          custody as a 5.6-fold rise, on a slightly broader measure than the audited
          note. ETF custody share is OSL&rsquo;s own figure.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- fragments -- */

function Bar({
  value,
  height,
  tone,
  growDelay,
  labelDelay,
}: {
  value: string;
  height: number;
  tone: 'prior' | 'current';
  growDelay: number;
  labelDelay: number;
}) {
  return (
    <div className="osl-barwrap">
      <div className="osl-barval osl-rise" style={at(labelDelay)}>
        {value}
      </div>
      <div
        className={`osl-bar osl-bar--${tone}`}
        style={
          {
            ['--osl-bar-h' as string]: `${height}px`,
            ['--osl-delay' as string]: `${growDelay}s`,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

function Connector({ delay }: { delay: number }) {
  return (
    <div className="osl-link">
      <div className="osl-link-line osl-draw-y" style={at(delay)} />
      <span className="osl-link-label osl-fade" style={at(delay + 0.1)}>
        Wholly owns
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------- css -- */

const CSS = `
.osl-root{
  --osl-font:'Poppins',system-ui,-apple-system,sans-serif;
  --osl-mono:var(--osl-font);
  --osl-ink:#1B2A33;
  --osl-ink-2:#3E5460;
  --osl-body:#5B6B75;
  --osl-ink-3:#8A99A3;
  --osl-rule:#E4E7EA;
  --osl-teal:#0f9993;
  --osl-teal-deep:#0d555c;
  --osl-teal-light:#a3e8e0;
  --osl-teal-wash:#ecfbf9;
  --osl-gold:#B08A2E;
  --osl-gold-deep:#7A5F1D;
  --osl-gold-wash:#faf5e9;
  --osl-ease:cubic-bezier(.22,.8,.3,1);

  position:relative;
  box-sizing:border-box;
  width:100%;
  max-width:1080px;
  background:#fff;
  border:1px solid var(--osl-rule);
  border-radius:20px;
  padding:40px 44px 28px;
  box-shadow:0 10px 20px -10px rgba(4,68,92,0.35);
  font-family:var(--osl-font);
  color:var(--osl-ink);
  -webkit-font-smoothing:antialiased;
}
.osl-root *,.osl-root *::before,.osl-root *::after{box-sizing:border-box;margin:0}
.osl-root h1,.osl-root h2,.osl-root h3,.osl-root p,.osl-root img,.osl-root figure,.osl-root figcaption{margin:0}
.osl-root p{letter-spacing:normal}
.osl-root .osl-accent{color:var(--osl-gold)}

.osl-root .osl-head{display:flex;justify-content:space-between;align-items:flex-start;gap:24px}
.osl-root .osl-logo{height:40px;width:auto;flex:none}
.osl-root.compact .osl-logo{display:none}

/* --- build animation ------------------------------------------------- */
.osl-root .osl-rise{opacity:0;transform:translateY(10px)}
.osl-root .osl-fade{opacity:0}
.osl-root .osl-draw-x{transform:scaleX(0);transform-origin:left center}
.osl-root .osl-draw-y{transform:scaleY(0);transform-origin:top center}
.osl-root .osl-bar{height:var(--osl-bar-h);clip-path:inset(100% 0 0 0)}

.osl-root[data-played='true'] .osl-rise{
  opacity:1;transform:none;
  transition:opacity .55s var(--osl-ease) var(--osl-delay,0s),
             transform .55s var(--osl-ease) var(--osl-delay,0s);
}
.osl-root[data-played='true'] .osl-fade{
  opacity:1;
  transition:opacity .55s var(--osl-ease) var(--osl-delay,0s);
}
.osl-root[data-played='true'] .osl-draw-x{
  transform:scaleX(1);
  transition:transform .5s var(--osl-ease) var(--osl-delay,0s);
}
.osl-root[data-played='true'] .osl-draw-y{
  transform:scaleY(1);
  transition:transform .32s var(--osl-ease) var(--osl-delay,0s);
}
.osl-root[data-played='true'] .osl-bar{
  clip-path:inset(0 0 0 0);
  transition:clip-path .85s cubic-bezier(.2,.75,.25,1) var(--osl-delay,0s);
}
@media (prefers-reduced-motion:reduce){
  .osl-root .osl-rise{opacity:1;transform:none}
  .osl-root .osl-fade{opacity:1}
  .osl-root .osl-draw-x{transform:scaleX(1)}
  .osl-root .osl-draw-y{transform:scaleY(1)}
  .osl-root .osl-bar{clip-path:none}
  .osl-root *{transition:none !important}
}

/* --- type ------------------------------------------------------------ */
.osl-root .osl-h1{font-size:32px;line-height:1.2;font-weight:700;letter-spacing:-.015em;max-width:560px}
.osl-root .osl-h1 span{display:block}
.osl-root .osl-standfirst{margin-top:16px;font-size:16px;line-height:1.62;color:var(--osl-body);max-width:40em;font-weight:400}
.osl-root .osl-eyebrow{font-size:12.5px;font-weight:500;letter-spacing:.085em;text-transform:uppercase;color:var(--osl-ink-3)}

.osl-root .osl-cols{display:grid;grid-template-columns:1fr 1.14fr;gap:56px;margin-top:40px;align-items:start}

/* --- money ----------------------------------------------------------- */
.osl-root .osl-hero{margin-top:12px;display:flex;align-items:baseline;gap:4px;color:var(--osl-teal-deep)}
.osl-root .osl-hero-cur{font-size:28px;font-weight:600;letter-spacing:-.01em}
.osl-root .osl-hero-num{font-size:64px;font-weight:700;letter-spacing:-.03em;line-height:1}
.osl-root .osl-hero-unit{font-size:26px;font-weight:600;letter-spacing:-.01em}
.osl-root .osl-hero-sub{margin-top:6px;font-size:15px;line-height:1.5;color:var(--osl-body);max-width:20em}

.osl-root .osl-plot{margin-top:34px;max-width:358px}
.osl-root .osl-bars{display:flex;align-items:flex-end;gap:21%;height:245px;padding-left:6px}
.osl-root .osl-barwrap{width:32%;display:flex;flex-direction:column;justify-content:flex-end;height:100%}
.osl-root .osl-barval{flex:0 0 auto;font-size:15px;font-weight:600;color:var(--osl-ink);text-align:center;margin-bottom:9px;letter-spacing:-.01em}
.osl-root .osl-bar{flex:0 0 auto;width:100%;border-radius:4px 4px 0 0}
.osl-root .osl-bar--prior{background:var(--osl-teal-light)}
.osl-root .osl-bar--current{background:var(--osl-teal)}
.osl-root .osl-axis{height:2px;background:#cfdada;width:calc(100% - 6px);margin-left:6px}
.osl-root .osl-xlabels{display:flex;gap:21%;padding-left:6px;margin-top:11px}
.osl-root .osl-xlabels span{width:32%;text-align:center;font-size:13px;letter-spacing:.06em;color:var(--osl-ink-3)}
.osl-root .osl-plot-note{margin-top:22px;font-size:13.5px;line-height:1.6;color:var(--osl-body);max-width:25em}

/* --- structure ------------------------------------------------------- */
.osl-root .osl-chain{margin-top:12px}
.osl-root .osl-node{border:1.5px solid var(--osl-rule);border-radius:12px;padding:16px 20px 17px;background:#fff}
.osl-root .osl-node-name{font-size:16.5px;font-weight:600;letter-spacing:-.012em;line-height:1.3}
.osl-root .osl-node-role{margin-top:6px;font-size:14px;line-height:1.55;color:var(--osl-body)}
.osl-root .osl-ticker{font-size:13px;color:var(--osl-ink-3);font-weight:400}
.osl-root .osl-node--group{background:#F5F8F8;border-color:#EEF1F2}
.osl-root .osl-node--group .osl-node-name{font-size:15.5px;color:var(--osl-ink-2)}
.osl-root .osl-node--licence{border-color:#e6d6ac;background:var(--osl-gold-wash);border-left:5px solid var(--osl-gold);padding-left:22px}
.osl-root .osl-node--custody{border-color:var(--osl-teal-light);background:var(--osl-teal-wash);border-left:5px solid var(--osl-teal);padding-left:22px}
.osl-root .osl-emph{color:var(--osl-teal-deep);font-weight:600}

.osl-root .osl-link{position:relative;height:30px;margin-left:30px}
.osl-root .osl-link-line{width:2px;height:100%;background:#d8e2e2}
.osl-root .osl-link-label{position:absolute;left:16px;top:50%;transform:translateY(-50%);font-size:11.5px;letter-spacing:.07em;text-transform:uppercase;color:var(--osl-ink-3);font-weight:500;white-space:nowrap}
.osl-root .osl-chain-note{margin-top:20px;font-size:13.5px;line-height:1.6;color:var(--osl-body)}

.osl-root .osl-tag{display:inline-block;font-size:11px;font-weight:500;letter-spacing:.08em;text-transform:uppercase;padding:3px 9px;border-radius:5px;margin-bottom:9px}
.osl-root .osl-tag--gold{background:#f0e4c4;color:var(--osl-gold-deep)}
.osl-root .osl-tag--teal{background:#d3f3ef;color:var(--osl-teal-deep)}

/* --- foot ------------------------------------------------------------ */
.osl-root .osl-foot{margin-top:40px}
.osl-root .osl-rule{height:1px;background:var(--osl-rule)}
.osl-root .osl-kicker{margin-top:20px;font-size:16px;line-height:1.6;font-weight:500;max-width:46em}
.osl-root .osl-kicker em{font-style:normal}
.osl-root .osl-src{margin-top:12px;font-family:var(--osl-mono);font-size:10px;line-height:1.6;color:var(--osl-ink-3)}

/* --- mid: blog-width two columns (container < 920px) ------------------ */
.osl-root.mid .osl-h1{font-size:28px}
.osl-root.mid .osl-standfirst{font-size:15px}
.osl-root.mid .osl-cols{grid-template-columns:1fr 1.25fr;gap:32px;margin-top:32px}
.osl-root.mid .osl-hero-cur{font-size:24px}
.osl-root.mid .osl-hero-num{font-size:52px}
.osl-root.mid .osl-hero-unit{font-size:22px}
.osl-root.mid .osl-hero-sub{font-size:14px}
.osl-root.mid .osl-plot{margin-top:28px}
.osl-root.mid .osl-barval{font-size:13px}
.osl-root.mid .osl-xlabels span{font-size:12px}
.osl-root.mid .osl-plot-note,.osl-root.mid .osl-chain-note{font-size:13px}
.osl-root.mid .osl-node{padding:14px 16px 15px}
.osl-root.mid .osl-node--licence,.osl-root.mid .osl-node--custody{padding-left:16px}
.osl-root.mid .osl-node-name{font-size:15.5px}
.osl-root.mid .osl-node--group .osl-node-name{font-size:14.5px}
.osl-root.mid .osl-node-role{font-size:13px}
.osl-root.mid .osl-foot{margin-top:32px}

/* --- narrow: single column (container < 720px) ------------------------ */
.osl-root.narrow .osl-cols{grid-template-columns:1fr;gap:40px;margin-top:32px}
.osl-root.narrow .osl-hero-num{font-size:56px}

/* --- compact: mobile (container < 640px) ------------------------------ */
.osl-root.compact{padding:26px 0 20px;border-radius:0;border-left:none;border-right:none;box-shadow:none}
.osl-root.compact .osl-h1{font-size:23px}
.osl-root.compact .osl-standfirst{font-size:14px}
.osl-root.compact .osl-hero-num{font-size:50px}
.osl-root.compact .osl-hero-cur{font-size:24px}
.osl-root.compact .osl-hero-unit{font-size:22px}
.osl-root.compact .osl-plot{max-width:280px}
.osl-root.compact .osl-bars,.osl-root.compact .osl-xlabels{padding-left:0}
.osl-root.compact .osl-axis{width:100%;margin-left:0}
.osl-root.compact .osl-node{padding:14px 16px 15px}
.osl-root.compact .osl-node--licence,.osl-root.compact .osl-node--custody{padding-left:16px}
.osl-root.compact .osl-kicker{font-size:15px}
`;
